import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import { clipAudioTimelineDuration, MIN_CLIP_PLAYBACK_SEC } from '@/lib/videoEditorPlayback';

/** Languages available for dubbing / translation audio lanes in the timeline. */
export const AUDIO_TRANSLATION_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ta', label: 'Tamil' },
  { value: 'kn', label: 'Kannada' },
  { value: 'mr', label: 'Marathi' },
  { value: 'de', label: 'German' },
];

const LABEL_BY_CODE = Object.fromEntries(
  AUDIO_TRANSLATION_LANGUAGES.map((o) => [o.value, o.label]),
);

export function audioTranslationLanguageLabel(code) {
  if (!code) return 'Audio';
  return LABEL_BY_CODE[String(code).toLowerCase()] || String(code);
}

export function localizedAudioForScene(scene, languageCode) {
  if (!scene || !languageCode) return null;
  const bag = scene.localized_audio || scene.localizedAudio;
  if (!bag || typeof bag !== 'object') return null;
  const entry = bag[languageCode] || bag[String(languageCode).toLowerCase()];
  return entry && typeof entry === 'object' ? entry : null;
}

/**
 * Build one audio timeline lane from segment localized_audio (or primary audio_url for source lane).
 */
export function buildTranslationAudioLaneFromScenes(scenes, sourceLane, languageCode, narrationProjectId) {
  const lang = String(languageCode || '').toLowerCase();
  const sceneById = Object.fromEntries((scenes || []).map((s) => [String(s.id), s]));
  const sourceByScene = Object.fromEntries(
    (sourceLane || [])
      .map((ac) => {
        const sid = String(ac.sceneId || '').replace(/^audio-/, '').replace(/^seg-audio-/, '').split('-seg-')[0];
        return sid ? [sid, ac] : null;
      })
      .filter(Boolean),
  );

  const out = [];
  (scenes || []).forEach((scene) => {
    const sid = scene?.id != null ? String(scene.id) : '';
    if (!sid) return;

    const loc = localizedAudioForScene(scene, lang);
    const audioUrl = loc?.audio_url || loc?.audioUrl;
    if (!audioUrl) return;

    const ref = sourceByScene[sid];
    const dur =
      Number(loc?.audio_duration || loc?.audioDuration)
      || Number(ref?.duration)
      || Number(scene.audio_duration || scene.duration)
      || 5;

    const clip = {
      id: `audio-${sid}__${lang}`,
      name: `${scene.title || `Segment ${scene.scene_number || ''}`.trim()} (${audioTranslationLanguageLabel(lang)})`,
      sceneId: sid,
      languageCode: lang,
      url: mediaUrlForPlayback(audioUrl),
      startTime: Number(ref?.startTime) || 0,
      duration: dur,
      sourceDuration: dur,
      volume: ref?.volume != null ? ref.volume : 0.8,
      muted: false,
      dubText: loc?.text || '',
    };
    if (narrationProjectId) {
      clip.waveformUrl = `/narration/projects/${narrationProjectId}/segments/${sid}/waveform?lang=${encodeURIComponent(lang)}`;
    }
    out.push(clip);
  });

  return out.sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));
}

/** Per-lane playback flags; defaults to all lanes on when unset. */
export function normalizeAudioLaneActiveFlags(timelineActive, laneCount) {
  const n = Math.max(1, Number(laneCount) || 1);
  const master = timelineActive?.audio !== false;
  const raw = Array.isArray(timelineActive?.audioLanes) ? timelineActive.audioLanes : null;
  const lanes = [];
  for (let i = 0; i < n; i += 1) {
    lanes.push(raw && raw[i] != null ? Boolean(raw[i]) : true);
  }
  return { master, lanes };
}

export function audioLanePlaybackMask(timelineActive, laneCount) {
  const { master, lanes } = normalizeAudioLaneActiveFlags(timelineActive, laneCount);
  if (!master) return lanes.map(() => false);
  return lanes;
}

export function normalizeSceneIdForAudioMatch(sceneId) {
  return String(sceneId || '').replace(/^audio-/, '').replace(/^seg-audio-/, '').split('-seg-')[0];
}

export function findAudioClipMatchingSceneInLane(lane, sceneId) {
  if (!Array.isArray(lane) || !lane.length || !sceneId) return null;
  const norm = normalizeSceneIdForAudioMatch(sceneId);
  return lane.find((ac) => {
    const raw = String(ac.sceneId ?? '').replace(/^audio-/, '').replace(/^seg-audio-/, '');
    const sid = raw.split('-seg-')[0];
    return sid === norm || String(ac.sceneId) === String(sceneId);
  }) || null;
}

/** Pair a video clip with narration audio on the currently active timeline lane(s). */
export function findPairedAudioClipForVideoOnActiveLanes(videoClip, audioTracks, timelineActive) {
  const linkedSceneId = videoClip?.parentSceneId ?? videoClip?.sceneId ?? videoClip?.id;
  if (!linkedSceneId || timelineActive?.audio === false) return null;
  const tracks = Array.isArray(audioTracks) ? audioTracks : [];
  const mask = audioLanePlaybackMask(timelineActive, tracks.length);

  if (videoClip.multishotPart != null) {
    const parentId = String(videoClip.parentSceneId ?? videoClip.sceneId ?? '');
    for (let i = tracks.length - 1; i >= 0; i -= 1) {
      if (!mask[i]) continue;
      const lane = Array.isArray(tracks[i]) ? tracks[i] : [];
      const byId = lane.find((ac) => ac.pairedVideoClipId === videoClip.id);
      if (byId) return byId;
      const byPart = lane.find((ac) => (
        String(ac.parentSceneId ?? normalizeSceneIdForAudioMatch(ac.sceneId)) === parentId
        && Number(ac.multishotPart) === Number(videoClip.multishotPart)
      ));
      if (byPart) return byPart;
    }
  }

  for (let i = tracks.length - 1; i >= 0; i -= 1) {
    if (!mask[i]) continue;
    const lane = Array.isArray(tracks[i]) ? tracks[i] : [];
    const match = findAudioClipMatchingSceneInLane(lane, linkedSceneId);
    if (match) return match;
  }
  return null;
}

/** Indices of audio lanes currently active for playback / timing reference. */
export function activeAudioLaneIndices(timelineActive, laneCount) {
  const mask = audioLanePlaybackMask(timelineActive, laneCount);
  return mask.map((on, i) => (on ? i : -1)).filter((i) => i >= 0);
}

/**
 * Lane used to position animation bars on the timeline.
 * Prefer original (lane 0) when it is active; otherwise the first active lane.
 * When all lanes are off, fall back to lane 0 for display alignment.
 */
export function resolveAnimationReferenceAudioLane({ audioTracks, timelineActive }) {
  const n = Math.max(1, (audioTracks || []).length);
  const active = activeAudioLaneIndices(timelineActive, n);
  if (!active.length) return 0;
  if (active.includes(0)) return 0;
  return active[0];
}

/** Start + span for one scene on a single audio lane (supports multi-segment scenes). */
export function sceneAudioBlockFromLane(sceneId, audioTracks, laneIndex, masterSpeed = 1) {
  const lane = (audioTracks || [])[laneIndex] || [];
  const sid = normalizeSceneIdForAudioMatch(sceneId);
  if (!sid) return null;
  const related = lane
    .filter((ac) => normalizeSceneIdForAudioMatch(ac?.sceneId) === sid)
    .sort((a, b) => (Number(a.startTime) || 0) - (Number(b.startTime) || 0));
  if (!related.length) return null;

  const multishotSlices = related.filter((ac) => ac.multishotPart != null);
  if (multishotSlices.length === 1) {
    const ac = multishotSlices[0];
    return {
      startTime: Number(ac.startTime) || 0,
      duration: Math.max(
        MIN_CLIP_PLAYBACK_SEC,
        clipAudioTimelineDuration(ac, 0, masterSpeed),
      ),
    };
  }

  const startTime = Number(related[0].startTime) || 0;
  let maxEnd = startTime;
  for (const ac of related) {
    const start = Number(ac.startTime) || 0;
    maxEnd = Math.max(maxEnd, start + clipAudioTimelineDuration(ac, 0, masterSpeed));
  }
  return {
    startTime,
    duration: Math.max(MIN_CLIP_PLAYBACK_SEC, maxEnd - startTime),
  };
}

export function audioLaneChoiceLabel(index, audioTrackMeta) {
  const meta = Array.isArray(audioTrackMeta) ? audioTrackMeta[index] : null;
  if (meta?.label) return String(meta.label);
  if (index === 0) return 'Original (Audio 1)';
  return `Audio ${index + 1}`;
}

/**
 * Pick which audio lane Magic Edit should use for segment duration + subtitle timing.
 * Multiple active lanes → caller should prompt the user.
 */
export function resolveMagicEditReferenceAudioLane({
  audioTracks,
  audioTrackMeta,
  timelineActive,
}) {
  const n = Math.max(1, (audioTracks || []).length);
  const active = activeAudioLaneIndices(timelineActive, n);
  if (!active.length) {
    return { laneIndex: 0, needsPrompt: false, activeLanes: [], choices: [] };
  }
  if (active.length === 1) {
    const laneIndex = active[0];
    return {
      laneIndex,
      needsPrompt: false,
      activeLanes: active,
      choices: [{ laneIndex, label: audioLaneChoiceLabel(laneIndex, audioTrackMeta) }],
    };
  }
  return {
    laneIndex: null,
    needsPrompt: true,
    activeLanes: active,
    choices: active.map((laneIndex) => ({
      laneIndex,
      label: audioLaneChoiceLabel(laneIndex, audioTrackMeta),
    })),
  };
}

/** Total narration/segment audio span for a scene on one audio lane. */
export function sceneAudioDurationFromLane(
  sceneId,
  videoStartTime,
  audioTracks,
  laneIndex,
  masterSpeed = 1,
) {
  const lane = (audioTracks || [])[laneIndex] || [];
  const sid = normalizeSceneIdForAudioMatch(sceneId);
  if (!sid) return null;
  const related = lane.filter((ac) => normalizeSceneIdForAudioMatch(ac?.sceneId) === sid);
  if (!related.length) return null;
  const multishotRelated = related.filter((ac) => ac?.multishotPart != null);
  if (multishotRelated.length) {
    const vStart = Number(videoStartTime) || 0;
    let chosen = null;
    let chosenDelta = Number.POSITIVE_INFINITY;
    for (const ac of multishotRelated) {
      const start = Number(ac.startTime) || 0;
      const end = start + clipAudioTimelineDuration(ac, 0, masterSpeed);
      if (vStart >= start - 0.02 && vStart <= end + 0.02) {
        chosen = ac;
        break;
      }
      const delta = Math.abs(start - vStart);
      if (delta < chosenDelta) {
        chosen = ac;
        chosenDelta = delta;
      }
    }
    return Math.max(
      MIN_CLIP_PLAYBACK_SEC,
      clipAudioTimelineDuration(chosen || multishotRelated[0], 0, masterSpeed),
    );
  }
  if (related.length === 1) {
    return Math.max(
      MIN_CLIP_PLAYBACK_SEC,
      clipAudioTimelineDuration(related[0], 0, masterSpeed),
    );
  }
  const vStart = Number(videoStartTime) || 0;
  let maxEnd = vStart;
  for (const ac of related) {
    const start = Number(ac.startTime) || 0;
    maxEnd = Math.max(maxEnd, start + clipAudioTimelineDuration(ac, 0, masterSpeed));
  }
  return Math.max(MIN_CLIP_PLAYBACK_SEC, maxEnd - vStart);
}

export function defaultAudioTrackMetaForLanes(audioTracks, existingMeta = []) {
  const tracks = audioTracks || [[]];
  const metaList = Array.isArray(existingMeta) ? existingMeta : [];
  return tracks.map((_, idx) => {
    const prev = metaList[idx];
    if (prev && typeof prev === 'object' && !Array.isArray(prev)) return prev;
    if (idx === 0) return { languageCode: null, label: 'Original', isTranslation: false };
    return { languageCode: null, label: `Audio ${idx + 1}`, isTranslation: false };
  });
}

export function mergeAudioTrackMeta(existingMeta, audioTracks) {
  const metaList = Array.isArray(existingMeta) ? existingMeta : [];
  const base = defaultAudioTrackMetaForLanes(audioTracks, metaList);
  return base.map((m, idx) => ({
    ...m,
    ...(metaList[idx] || {}),
    label: (metaList[idx]?.label || m.label),
  }));
}
